import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraDatePicker } from "../../../../components/AtiraDatePicker";
import { AtiraSelect } from "../../../../components/AtiraSelect";
import { Drawer } from "../../../../components/Drawer";
import { Flex } from "../../../../components/Flex";
import { Info } from "../../../../components/Info";
import { Input } from "../../../../components/Input";
import { Text } from "../../../../components/Text";
import { WhatsappCampaignDetailsResponseDto } from "../../../../model/integration/dto/whatsapp-campaign/WhatsappCampaignDetailsResponseDto";
import { WhatsappCampaignLimits } from "../../../../model/integration/types/whatsapp/WhatsappCampaignLimits";
import { WhatsappCampaignRecipientStatus } from "../../../../model/integration/types/whatsapp/WhatsappCampaignRecipientStatus.enum";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Breakpoints } from "../../../../theme/Breakpoints";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { Checkbox, Radio } from "../../../../utils/local-antd";
import { DrawerDefaultsExtra } from "../../../tasks/components/DrawerDefaultsExtra";
import { WATemplateContent } from "../whatsapp-chat/components/WATemplateContent";
import {
  templateVariables,
  variableReady,
} from "../whatsapp-chat/utils/templateVariables";
import { TestIDs } from "../../../../tests/types/testIds";
import { CampaignPick } from "./types/CampaignPick";
import { campaignInstant, formatCampaignTime } from "./utils/campaignTime";
import { WACampaignRecipientsPicker } from "./WACampaignRecipientsPicker";
import {
  campaignParametersFrom,
  campaignValuesFrom,
  canUseContactName,
} from "./utils/campaignParameters";

const Section = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
`;

const Preview = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  border: 1px solid ${({ theme }) => theme.lightgray};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.lightSub};
`;

const GapRow = styled(Flex)`
  align-items: flex-end;
  gap: ${Spacing.s};
  max-width: 28rem;
`;

const ExistingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${Spacing.xs};

  @media (max-width: ${Breakpoints.PHONE}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Existing = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.s};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.md};
  border: 1px solid ${({ theme }) => theme.lightgray};
`;

const GAP_UNITS = { seconds: 1, minutes: 60, hours: 60 * 60 } as const;

const DRAWER_SIZE = "min(100vw, 64rem)";

type GapUnit = keyof typeof GAP_UNITS;

type CampaignForm = {
  name: string;
  picks: CampaignPick[];
  removedIds: string[];
  templateId?: string;
  /** Kept whole: variable keys like "body:1" are not form paths */
  variableValues: Record<string, string>;
  fromContact: string[];
  startLater: boolean;
  startAt: Date | string | null;
  gapValue: string;
  gapUnit: GapUnit;
};

const EMPTY_FORM: CampaignForm = {
  name: "",
  picks: [],
  removedIds: [],
  templateId: undefined,
  variableValues: {},
  fromContact: [],
  startLater: false,
  startAt: null,
  gapValue: "2",
  gapUnit: "minutes",
};

const splitGap = (seconds: number): { value: number; unit: GapUnit } => {
  if (seconds % GAP_UNITS.hours === 0) {
    return { value: seconds / GAP_UNITS.hours, unit: "hours" };
  }

  if (seconds % GAP_UNITS.minutes === 0) {
    return { value: seconds / GAP_UNITS.minutes, unit: "minutes" };
  }

  return { value: seconds, unit: "seconds" };
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Editing this one; creating when absent */
  campaign?: WhatsappCampaignDetailsResponseDto;
  /** Entries picked in the entries table */
  initialPicks?: CampaignPick[];
  onSaved?: () => void;
};

export const WACampaignCreateEditDrawer: React.FC<Props> = ({
  open,
  onClose,
  campaign,
  initialPicks,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { dirtyFields },
  } = useForm<CampaignForm>({
    defaultValues: EMPTY_FORM,
  });

  const {
    name,
    picks,
    removedIds,
    templateId,
    variableValues: values,
    fromContact,
    startLater,
    startAt,
    gapValue,
    gapUnit,
  } = watch();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const timezone = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.settings?.timezone;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const templates = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaTemplates,
  );
  const numberHealth = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaNumberHealth,
  );

  const approved = useMemo(
    () =>
      (templates || []).filter(
        (template: any) => template.status === "APPROVED",
      ),
    [templates],
  );

  const template =
    approved.find((candidate: any) => candidate.id === templateId) ||
    (campaign?.template.id === templateId ? campaign?.template : undefined);

  const variables = useMemo(() => templateVariables(template), [template]);

  const recipients = campaign?.recipients || [];
  const started = recipients.some(({ sentAt }) => sentAt);
  const kept = recipients.filter(({ _id }) => !removedIds.includes(_id));
  const total = kept.length + picks.length;
  const room = WhatsappCampaignLimits.MAX_RECIPIENTS - total;
  const gapSeconds = Number(gapValue) * GAP_UNITS[gapUnit];

  useEffect(() => {
    if (!open || !integration?._id) {
      return;
    }

    dispatch(
      integrationActions.getWhatsaAppMetaTemplates({
        userId,
        integrationId: integration._id,
      }),
    );

    if (!numberHealth) {
      dispatch(
        integrationActions.getWhatsappMetaNumberHealth({
          userId,
          integrationId: integration._id,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, integration?._id]);

  useEffect(() => {
    if (!open) {
      reset(EMPTY_FORM);
      return;
    }

    if (campaign) {
      const saved = campaignValuesFrom(
        campaign.template,
        campaign.templateParameters,
      );
      const gap = splitGap(campaign.gapSeconds);

      reset({
        ...EMPTY_FORM,
        name: campaign.name,
        templateId: campaign.template.id,
        variableValues: saved.values,
        fromContact: saved.fromContact,
        startLater: dayjs(campaign.startAt).isAfter(dayjs()),
        startAt: campaign.startAt,
        gapValue: String(gap.value),
        gapUnit: gap.unit,
      });
      return;
    }

    reset({ ...EMPTY_FORM, picks: initialPicks || [] });
  }, [open, campaign, initialPicks, reset]);

  const missing = variables.some(
    (variable) => !variableReady(variable, values[variable.key]),
  );

  const gapValid =
    Number.isInteger(gapSeconds) &&
    gapSeconds >= WhatsappCampaignLimits.MIN_GAP_SECONDS &&
    gapSeconds <= WhatsappCampaignLimits.MAX_GAP_SECONDS;

  const problem = !name.trim()
    ? "name"
    : !total
      ? "recipients"
      : total > WhatsappCampaignLimits.MAX_RECIPIENTS
        ? "tooMany"
        : !template
          ? "template"
          : missing
            ? "variables"
            : !gapValid
              ? "gap"
              : startLater && !startAt
                ? "start"
                : undefined;

  const chosenStart = () =>
    startLater && startAt ? campaignInstant(startAt, timezone) : dayjs();
  const firstSendAt =
    startLater && startAt
      ? campaignInstant(startAt, timezone)
      : dayjs().add(5, "second");
  const unsent =
    kept.filter(
      ({ status }) => status === WhatsappCampaignRecipientStatus.PENDING,
    ).length + picks.length;
  const finishAt = firstSendAt.add(Math.max(unsent - 1, 0) * gapSeconds, "s");

  const previewValues = useMemo(
    () => ({
      ...values,
      ...Object.fromEntries(
        fromContact.map((key) => [
          key,
          `[${t("integration.whatsapp.meta.campaign.drawer.contactName")}]`,
        ]),
      ),
    }),
    [values, fromContact, t],
  );

  const onSelectTemplate = (id: string) => {
    setValue("templateId", id);
    setValue("variableValues", {});
    setValue("fromContact", []);
  };

  const onSave = async () => {
    if (problem || !template || !integration) {
      return;
    }

    const recipientsOf = (list: CampaignPick[]) => ({
      conversationIds: list
        .filter(({ kind }) => kind === "chat")
        .map(({ id }) => id),
      entryIds: list.filter(({ kind }) => kind === "entry").map(({ id }) => id),
    });

    const templateParameters = campaignParametersFrom(
      variables,
      values,
      fromContact,
    );

    try {
      setLoading(true);

      if (campaign) {
        await dispatch(
          integrationActions.updateWhatsappMetaCampaign({
            userId,
            integrationId: integration._id,
            campaignId: campaign._id,
            name: name.trim(),
            template,
            templateParameters,
            gapSeconds,
            // Only when the owner changed it: sent anyway, a first message
            // going out while the editor was open would get the whole save
            // refused as already started
            ...(!started && (dirtyFields.startLater || dirtyFields.startAt)
              ? { startAt: chosenStart().toISOString() }
              : {}),
            ...(picks.length ? { addRecipients: recipientsOf(picks) } : {}),
            ...(removedIds.length ? { removeRecipientIds: removedIds } : {}),
          }),
        ).unwrap();

        AtiraToast.success(t("integration.whatsapp.meta.campaign.saved"));
      } else {
        await dispatch(
          integrationActions.createWhatsappMetaCampaign({
            userId,
            integrationId: integration._id,
            name: name.trim(),
            template,
            templateParameters,
            startAt: chosenStart().toISOString(),
            gapSeconds,
            recipients: recipientsOf(picks),
          }),
        ).unwrap();

        AtiraToast.success(t("integration.whatsapp.meta.campaign.created"));
      }

      onSaved?.();
      onClose();
    } catch (e) {
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={t(
        campaign
          ? "integration.whatsapp.meta.campaign.drawer.editTitle"
          : "integration.whatsapp.meta.campaign.drawer.createTitle",
      )}
      open={open}
      onClose={onClose}
      size={DRAWER_SIZE}
      destroyOnHidden
      extra={
        <DrawerDefaultsExtra
          onSubmit={onSave}
          onCancel={onClose}
          config={{ loading, disabled: Boolean(problem) }}
          submitTestId={TestIDs.integration.whatsappMeta.campaign.editor.submit}
          submitTitle={t(campaign ? "common.save" : "common.create")}
        />
      }
    >
      <Flex flexDirection="column" gap="l" width="100%">
        <Info info={t("integration.whatsapp.meta.campaign.drawer.optIn")} />

        {campaign && started ? (
          <Info
            color="warning"
            info={t("integration.whatsapp.meta.campaign.drawer.editForward")}
          />
        ) : null}

        <Controller
          name="name"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Input
              title={t("integration.whatsapp.meta.campaign.drawer.name")}
              value={value}
              onChange={onChange}
              placeholder={t(
                "integration.whatsapp.meta.campaign.drawer.namePlaceholder",
              )}
              dataTestId={TestIDs.integration.whatsappMeta.campaign.editor.name}
              required
            />
          )}
        />

        <Section>
          <Text fontWeight="bold">
            {t("integration.whatsapp.meta.campaign.drawer.recipients", {
              count: total,
              max: WhatsappCampaignLimits.MAX_RECIPIENTS,
            })}
          </Text>

          {kept.length ? (
            <ExistingGrid>
              {kept.map((recipient) => (
                <Existing key={recipient._id}>
                  <Flex flexDirection="column" style={{ minWidth: 0 }}>
                    <Text fontSize="s" wordBreak="break-word">
                      {recipient.name || `+${recipient.waId}`}
                    </Text>
                    <Text fontSize="xs" color="darkerSub">
                      {`+${recipient.waId}`} ·{" "}
                      {t(
                        `integration.whatsapp.meta.campaign.recipient.status.${recipient.status}`,
                      )}
                    </Text>
                  </Flex>

                  {recipient.status ===
                  WhatsappCampaignRecipientStatus.PENDING ? (
                    <Checkbox
                      checked
                      onChange={() =>
                        setValue("removedIds", [...removedIds, recipient._id])
                      }
                    />
                  ) : null}
                </Existing>
              ))}
            </ExistingGrid>
          ) : null}

          <WACampaignRecipientsPicker
            picks={picks}
            onChange={(next) => setValue("picks", next)}
            room={room}
            takenWaIds={kept.map(({ waId }) => waId)}
          />

          <Text fontSize="xs" color="darkerSub">
            {t("integration.whatsapp.meta.campaign.drawer.recipientsNote")}
          </Text>
        </Section>

        <Section>
          <AtiraSelect
            title={t("integration.whatsapp.meta.campaign.drawer.template")}
            value={templateId}
            onChange={onSelectTemplate}
            placeholder={t(
              "integration.whatsapp.meta.campaign.drawer.templatePlaceholder",
            )}
            options={approved.map((candidate: any) => ({
              value: candidate.id,
              label: `${candidate.name} · ${candidate.language}`,
              "data-testid":
                TestIDs.integration.whatsappMeta.campaign.editor.template.option(
                  candidate.id,
                ),
            }))}
            className={
              TestIDs.integration.whatsappMeta.campaign.editor.template.open
            }
            showSearch
            optionFilterProp="label"
            required
          />

          {template ? (
            <Preview>
              <WATemplateContent
                components={template.components}
                values={previewValues}
              />
            </Preview>
          ) : null}

          {variables.map((variable) => (
            <Flex key={variable.key} flexDirection="column" gap="xs">
              <Input
                value={values[variable.key] || ""}
                onChange={(e) =>
                  setValue("variableValues", {
                    ...values,
                    [variable.key]: e.target.value,
                  })
                }
                title={t(
                  variable.area === "location"
                    ? `integration.whatsapp.meta.templates.modal.variable.location.${variable.name}`
                    : `integration.whatsapp.meta.templates.modal.variable.${variable.area}`,
                  { name: variable.name },
                )}
                placeholder={
                  fromContact.includes(variable.key)
                    ? t(
                        "integration.whatsapp.meta.campaign.drawer.fallbackPlaceholder",
                      )
                    : variable.example ||
                      t(
                        "integration.whatsapp.meta.templates.modal.variable.placeholder",
                      )
                }
                required={!variable.optional}
                dataTestId={TestIDs.integration.whatsappMeta.campaign.editor.variable(
                  variable.key,
                )}
              />

              {canUseContactName(variable) ? (
                <Checkbox
                  data-testid={TestIDs.integration.whatsappMeta.campaign.editor.useContactName(
                    variable.key,
                  )}
                  checked={fromContact.includes(variable.key)}
                  onChange={(e) =>
                    setValue(
                      "fromContact",
                      e.target.checked
                        ? [...fromContact, variable.key]
                        : fromContact.filter((key) => key !== variable.key),
                    )
                  }
                >
                  <Text fontSize="s">
                    {t(
                      "integration.whatsapp.meta.campaign.drawer.useContactName",
                    )}
                  </Text>
                </Checkbox>
              ) : null}
            </Flex>
          ))}
        </Section>

        <Section>
          <Text fontWeight="bold">
            {t("integration.whatsapp.meta.campaign.drawer.schedule")}
          </Text>

          <Controller
            name="startLater"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Radio.Group
                value={value ? "later" : "now"}
                onChange={(e) => onChange(e.target.value === "later")}
                disabled={started}
              >
                <Radio value="now">
                  {t("integration.whatsapp.meta.campaign.drawer.startNow")}
                </Radio>
                <Radio
                  value="later"
                  data-testid={
                    TestIDs.integration.whatsappMeta.campaign.editor.startLater
                  }
                >
                  {t("integration.whatsapp.meta.campaign.drawer.startLater")}
                </Radio>
              </Radio.Group>
            )}
          />

          {startLater ? (
            <Controller
              name="startAt"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AtiraDatePicker
                  title={t("integration.whatsapp.meta.campaign.drawer.startAt")}
                  value={value}
                  onChange={onChange}
                  showTime
                  fromToday
                  disabled={started}
                  dataTestId={
                    TestIDs.integration.whatsappMeta.campaign.editor.startAt
                  }
                />
              )}
            />
          ) : null}

          <GapRow>
            <Flex flex={1}>
              <Controller
                name="gapValue"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Input
                    type="number"
                    title={t("integration.whatsapp.meta.campaign.drawer.gap")}
                    info={t(
                      "integration.whatsapp.meta.campaign.drawer.gapInfo",
                    )}
                    value={value}
                    onChange={onChange}
                    valid={gapValid}
                    dataTestId={
                      TestIDs.integration.whatsappMeta.campaign.editor.gap
                    }
                    required
                  />
                )}
              />
            </Flex>

            <Controller
              name="gapUnit"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AtiraSelect
                  value={value}
                  onChange={onChange}
                  options={Object.keys(GAP_UNITS).map((unit) => ({
                    value: unit,
                    label: t(`integration.whatsapp.meta.campaign.unit.${unit}`),
                    "data-testid":
                      TestIDs.integration.whatsappMeta.campaign.editor.gapUnit.option(
                        unit,
                      ),
                  }))}
                  className={
                    TestIDs.integration.whatsappMeta.campaign.editor.gapUnit
                      .open
                  }
                  width="10rem"
                />
              )}
            />
          </GapRow>

          {!gapValid ? (
            <Text fontSize="s" color="danger">
              {t("integration.whatsapp.meta.campaign.drawer.gapRange")}
            </Text>
          ) : unsent ? (
            <Text fontSize="s" color="darkerSub">
              {t("integration.whatsapp.meta.campaign.drawer.finishAt", {
                date: formatCampaignTime(finishAt, timezone),
              })}
            </Text>
          ) : null}
        </Section>

        {numberHealth?.messagingLimitTier ? (
          <Info
            info={t("integration.whatsapp.meta.campaign.drawer.dailyCap", {
              tier: t(
                `integration.whatsapp.meta.health.tier.${numberHealth.messagingLimitTier}`,
                { defaultValue: numberHealth.messagingLimitTier },
              ),
            })}
          />
        ) : null}

        {problem ? (
          <Text fontSize="s" color="darkerSub">
            {t(`integration.whatsapp.meta.campaign.drawer.problem.${problem}`, {
              max: WhatsappCampaignLimits.MAX_RECIPIENTS,
            })}
          </Text>
        ) : null}
      </Flex>
    </Drawer>
  );
};
